import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DonorInstitutionWithDonations } from '../../../api/types/donations/donor-institution';
import dateFormat from '../../../utils/date-format';
import { DonationStatus } from '../../../api/types/donations/donor-institution';
import { cancelDonationOperation } from '../operations/cancel-donation-operation';

interface DonorDetailsDialogProps {
  visible: boolean;
  donor: DonorInstitutionWithDonations | null;
  onHide: () => void;
  onEdit: (donor: DonorInstitutionWithDonations) => void;
  onDelete: (donorId: number) => void;
  onAddDonation: (donor: DonorInstitutionWithDonations) => void;
  deletingId: number | null;
  onRefresh?: () => void;
}

export const DonorDetailsDialog: React.FC<DonorDetailsDialogProps> = ({
  visible,
  donor,
  onHide,
  onEdit,
  onDelete,
  onAddDonation,
  deletingId,
  onRefresh,
}) => {
  const [cancellingDonationId, setCancellingDonationId] = useState<
    number | null
  >(null);

  if (!donor) return null;

  const getStatusLabel = (status: DonationStatus) => {
    switch (status) {
      case DonationStatus.PENDING:
        return 'ממתין';
      case DonationStatus.FULFILLED:
        return 'מולא';
      case DonationStatus.CANCELLED:
        return 'בוטל';
      default:
        return status;
    }
  };

  const getStatusSeverity = (status: DonationStatus) => {
    switch (status) {
      case DonationStatus.PENDING:
        return 'warning';
      case DonationStatus.FULFILLED:
        return 'success';
      case DonationStatus.CANCELLED:
        return 'danger';
      default:
        return 'info';
    }
  };

  const paymentMethodLabel = (method: string | null) => {
    switch (method) {
      case 'bank_transfer':
        return 'העברה בנקאית';
      case 'check':
        return "צ'ק";
      case 'credit_card':
        return 'כרטיס אשראי';
      default:
        return '-';
    }
  };

  const donationReceiptsBody = (donation: any) => {
    const receipts = donation.receipts || [];
    const originalReceipt = receipts.find((r: any) => !r.isCancellation);
    const cancellationReceipt = receipts.find((r: any) => r.isCancellation);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {originalReceipt && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem',
              backgroundColor: '#f0f9ff',
              borderRadius: '4px',
            }}
          >
            <i className="pi pi-file" style={{ color: '#2196F3' }}></i>
            <span style={{ fontSize: '0.875rem' }}>
              קבלה: {originalReceipt.receiptNumber}
            </span>
            {originalReceipt.fileUrl && (
              <Button
                icon="pi pi-download"
                text
                size="small"
                onClick={() => window.open(originalReceipt.fileUrl, '_blank')}
                tooltip="הורדת קבלה"
              />
            )}
          </div>
        )}
        {cancellationReceipt && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem',
              backgroundColor: '#fef2f2',
              borderRadius: '4px',
            }}
          >
            <i className="pi pi-times-circle" style={{ color: '#ef4444' }}></i>
            <span style={{ fontSize: '0.875rem' }}>
              ביטול: {cancellationReceipt.receiptNumber}
            </span>
            {cancellationReceipt.fileUrl && (
              <Button
                icon="pi pi-download"
                text
                size="small"
                onClick={() =>
                  window.open(cancellationReceipt.fileUrl, '_blank')
                }
                tooltip="הורדת קבלת ביטול"
              />
            )}
          </div>
        )}
        {receipts.length === 0 && (
          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            אין קבלות
          </span>
        )}
      </div>
    );
  };

  const donationAmountBody = (donation: any) => {
    const amount =
      typeof donation.amount === 'number'
        ? donation.amount
        : parseFloat(donation.amount) || 0;
    return `${amount.toFixed(2)} ${donation.currency || 'ILS'}`;
  };

  const handleCancelDonation = (donation: any) => {
    confirmDialog({
      message: `האם אתה בטוח שברצונך לבטל את התרומה מספר ${donation.donationNumber}?`,
      header: 'אישור ביטול תרומה',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'ביטול תרומה',
      rejectLabel: 'חזור',
      acceptClassName: 'p-button-danger',
      accept: () => {
        cancelDonationOperation(
          donor.id,
          donation.id,
          setCancellingDonationId,
          () => {
            if (onRefresh) {
              onRefresh();
            }
          },
        );
      },
    });
  };

  const donationActionsBody = (donation: any) => {
    // Check if status is 'fulfilled' (handle both enum and string)
    const isFulfilled =
      donation.status === DonationStatus.FULFILLED ||
      donation.status === 'fulfilled';

    if (isFulfilled) {
      const isCurrentlyCancelling = cancellingDonationId === donation.id;
      const isAnyCancelling = cancellingDonationId !== null;

      return (
        <Button
          icon="pi pi-times"
          text
          severity="danger"
          tooltip="ביטול תרומה"
          loading={isCurrentlyCancelling}
          disabled={isAnyCancelling}
          onClick={(e) => {
            e.stopPropagation();
            if (!isAnyCancelling) {
              handleCancelDonation(donation);
            }
          }}
        />
      );
    }
    return <span style={{ color: '#9ca3af' }}>-</span>;
  };

  return (
    <>
      <ConfirmDialog />
      <Dialog
        header={`פרטי תורם: ${donor.name}`}
        visible={visible}
        onHide={onHide}
        style={{
          width: '90vw',
          maxWidth: '1200px',
          direction: 'rtl',
        }}
        modal
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                label="עריכה"
                icon="pi pi-pencil"
                severity="warning"
                onClick={() => {
                  onEdit(donor);
                  onHide();
                }}
              />
              <Button
                label="מחיקה"
                icon="pi pi-trash"
                severity="danger"
                loading={deletingId === donor.id}
                disabled={deletingId !== null}
                onClick={() => onDelete(donor.id)}
              />
            </div>
            <Button
              label="סגירה"
              icon="pi pi-times"
              severity="secondary"
              text
              onClick={onHide}
            />
          </div>
        }
      >
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
        >
          {/* Donor Details Section */}
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>פרטי התורם</h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1rem',
              }}
            >
              <div>
                <strong>שם:</strong> {donor.name}
              </div>
              {donor.taxExemptId && (
                <div>
                  <strong>מספר פטור ממס:</strong> {donor.taxExemptId}
                </div>
              )}
              {donor.primaryEmail && (
                <div>
                  <strong>דוא"ל:</strong> {donor.primaryEmail}
                </div>
              )}
              {donor.phone && (
                <div>
                  <strong>טלפון:</strong> {donor.phone}
                </div>
              )}
              {donor.website && (
                <div>
                  <strong>אתר אינטרנט:</strong>{' '}
                  <a
                    href={donor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {donor.website}
                  </a>
                </div>
              )}
              {donor.addressLine1 && (
                <div>
                  <strong>כתובת:</strong> {donor.addressLine1}
                </div>
              )}
              {donor.city && (
                <div>
                  <strong>עיר:</strong> {donor.city}
                </div>
              )}
              <div>
                <strong>מדינה:</strong> {donor.country}
              </div>
              <div>
                <strong>תאריך יצירה:</strong>{' '}
                {dateFormat.displayFormatDate(new Date(donor.createdAt))}
              </div>
            </div>
          </div>

          {/* Donations Section */}
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
              }}
            >
              <h3 style={{ margin: 0 }}>תרומות</h3>
              <Button
                label="הוספת תרומה"
                icon="pi pi-plus"
                onClick={() => {
                  onAddDonation(donor);
                  onHide();
                }}
              />
            </div>
            <DataTable
              value={donor.donations || []}
              emptyMessage="אין תרומות"
              className="p-datatable-striped"
            >
              <Column
                field="donationNumber"
                header="מספר תרומה"
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="amount"
                header="סכום"
                sortable
                style={{ textAlign: 'right' }}
                body={donationAmountBody}
              />
              <Column
                field="paymentDate"
                header="תאריך תשלום"
                sortable
                style={{ textAlign: 'right' }}
                body={(donation) =>
                  dateFormat.displayFormatDate(new Date(donation.paymentDate))
                }
              />
              <Column
                field="paymentMethod"
                header="אמצעי תשלום"
                sortable
                style={{ textAlign: 'right' }}
                body={(donation) => paymentMethodLabel(donation.paymentMethod)}
              />
              <Column
                field="status"
                header="סטטוס"
                sortable
                style={{ textAlign: 'right' }}
                body={(donation) => (
                  <Tag
                    value={getStatusLabel(donation.status)}
                    severity={getStatusSeverity(donation.status)}
                  />
                )}
              />
              <Column
                field="purpose"
                header="מטרה"
                sortable
                style={{ textAlign: 'right' }}
                body={(donation) => donation.purpose || '-'}
              />
              <Column
                header="קבלות"
                body={donationReceiptsBody}
                style={{ textAlign: 'right', minWidth: '200px' }}
              />
              <Column
                header="פעולות"
                body={donationActionsBody}
                style={{ textAlign: 'center', width: '100px' }}
              />
            </DataTable>
          </div>
        </div>
      </Dialog>
    </>
  );
};
